/**
 * Utility functions for handling temperature ranges
 */

// Standard temperature ranges with their min/max values
export const STANDARD_TEMP_RANGES = [
  { label: 'Cryogenic', value: 'cryogenic', min: -180, max: -150, unit: 'C' },
  {
    label: 'Ultra Low Freezer',
    value: 'ultra_low_freezer',
    min: -90,
    max: -60,
    unit: 'C',
  },
  { label: 'Deep Frozen', value: 'deep_frozen', min: -40, max: -20, unit: 'C' },
  { label: 'Frozen', value: 'frozen', min: -25, max: -15, unit: 'C' },
  { label: 'Refrigerated', value: 'refrigerated', min: 2, max: 8, unit: 'C' },
  {
    label: 'Controlled Room Temperature (CRT)',
    value: 'crt',
    min: 15,
    max: 25,
    unit: 'C',
  },
  { label: 'Ambient', value: 'ambient', min: 15, max: 30, unit: 'C' },
];

/**
 * Determines the temperature range type based on min/max temperature values
 *
 * @param {number|null} minTemp - Minimum temperature value
 * @param {number|null} maxTemp - Maximum temperature value
 * @param {string} unit - Temperature unit ('C' or 'F')
 * @returns {Object} - Temperature range information
 */
export function determineTemperatureRange(minTemp, maxTemp, unit = 'C') {
  // Handle null/undefined values
  if (
    minTemp === null ||
    maxTemp === null ||
    minTemp === undefined ||
    maxTemp === undefined ||
    minTemp === '' ||
    maxTemp === ''
  ) {
    return { type: 'not_specified', label: 'Not Specified', value: 'none' };
  }

  // Convert to numbers if they're strings
  minTemp = Number(minTemp);
  maxTemp = Number(maxTemp);

  // Convert to Celsius if needed for comparison
  const minTempC = convertToCelsius(minTemp, unit);
  const maxTempC = convertToCelsius(maxTemp, unit);

  // Check if values match any standard range (with some tolerance)
  const tolerance = 1; // Allow 1 degree tolerance

  for (const range of STANDARD_TEMP_RANGES) {
    if (
      Math.abs(minTempC - range.min) <= tolerance &&
      Math.abs(maxTempC - range.max) <= tolerance
    ) {
      return {
        type: 'standard',
        label: range.label,
        value: range.value,
        min: minTemp,
        max: maxTemp,
        unit: unit,
      };
    }
  }

  // If no standard range matches, it's custom
  return {
    type: 'custom',
    label: 'Custom Range',
    value: 'custom',
    customDisplay: `${minTemp}°${unit} to ${maxTemp}°${unit}`,
    min: minTemp,
    max: maxTemp,
    unit: unit,
  };
}

/**
 * Converts temperature to Celsius if needed
 *
 * @param {number} temp - Temperature value
 * @param {string} unit - Temperature unit ('C' or 'F')
 * @returns {number} - Temperature in Celsius
 */
export function convertToCelsius(temp, unit) {
  if (unit === 'F') {
    return ((temp - 32) * 5) / 9;
  }
  return temp; // Already Celsius
}

/**
 * Formats temperature range for display
 *
 * @param {number|null} minTemp - Minimum temperature value
 * @param {number|null} maxTemp - Maximum temperature value
 * @param {string} unit - Temperature unit ('C' or 'F')
 * @returns {string} - Formatted temperature range string
 */
export function displayTemperatureRange(minTemp, maxTemp, unit = 'C') {
  const rangeInfo = determineTemperatureRange(minTemp, maxTemp, unit);

  switch (rangeInfo.type) {
    case 'standard':
      return `${rangeInfo.label} (${minTemp}°${unit} to ${maxTemp}°${unit})`;
    case 'custom':
      return `Custom: ${rangeInfo.customDisplay}`;
    case 'not_specified':
    default:
      return 'Not Specified';
  }
}
